import {
  isTransientFailure,
  TRANSIENT_RETRY_LIMIT,
} from '@aspid/api-client';
import { queryOptions } from '@tanstack/react-query';


import { queries } from '@api';
import { ApiResponseError } from '@api/apiUtils/ApiResponseError';
import { retrieveActiveUser } from '@api/users/api.users.requests';


/**
 * Коды axios, означающие «мы уже прождали таймаут».
 */
const TIMEOUT_CAUSES = new Set(['ECONNABORTED', 'ETIMEDOUT']);

/**
 * Отказ по таймауту не повторяется, и это НЕ исключение из правила, а защита
 * от него же.
 *
 * Таймаут запроса — 60 с. Повторить его дважды значит держать человека перед
 * спиннером до ~3 минут и только потом показать ошибку: хуже, чем прежний
 * мгновенный отказ, который правка и чинила (найдено adversarial-ревью
 * AF-537). Мгновенные отказы (`ERR_NETWORK`) повторять дёшево — экран
 * появится через пару секунд, — а «висящее» соединение дешёвым повтором не
 * лечится.
 */
const isTimeoutFailure = (error: unknown): boolean =>
  error instanceof ApiResponseError &&
  error.type !== undefined &&
  TIMEOUT_CAUSES.has(error.type);

/**
 * Стартовая проба авторизации.
 *
 * `retry` — предикат, а не флаг (AF-537). Эти настройки читает `useActiveUser`
 * через `useSuspenseQuery`, а suspense-запрос по построению бросает ошибку в
 * ближайший `ErrorBoundary`. С прежним `retry: false` это означало: один сетевой
 * сбой пробы — и вместо приложения страница ошибки. В Sentry (`flow-analist`)
 * это дало 41 событие, и во всех разобранных replay падал именно этот запрос —
 * на трёх разных маршрутах и трёх релизах.
 *
 * Граница «повторять / не повторять» не изобретается здесь: она общая с `deals`
 * и живёт в `@aspid/api-client`. Осознанный отказ бэкенда (`4xx`, и прежде всего
 * 401 у гостя) не повторяется — повтор его не изменит, а на каждом анонимном
 * заходе утроил бы нагрузку.
 *
 * Чего это не чинит: ОКОНЧАТЕЛЬНЫЙ отказ по-прежнему уводит экран в
 * `ErrorBoundary` — повторы убирают икоту, а не лежащий бэкенд. И повторы
 * НЕ бесплатны по времени, поэтому таймауты из них исключены (см. ниже).
 */
export const getActiveUserOptions = queryOptions({
  queryKey: queries.users.activeUser.queryKey,
  queryFn: () => retrieveActiveUser(),
  gcTime: Infinity,
  staleTime: Infinity,
  /*
   * Аннотация `ApiResponseError` — для вывода типов (`useSuspenseQuery`
   * объявляет тот же TError, и `unknown` здесь рвёт цепочку). Рантайм она НЕ
   * гарантирует: бросить могут что угодно, поэтому оба предиката ниже сами
   * проверяют класс. Это не дублирование, а разделение — тип для компилятора,
   * проверка для исполнения.
   */
  retry: (failureCount: number, error: ApiResponseError) =>
    failureCount < TRANSIENT_RETRY_LIMIT &&
    isTransientFailure(error) &&
    !isTimeoutFailure(error),
});
